/** 图层事件列表编辑：on + action 联合类型表单。 */
import { Button, Input, Select, Space, Switch } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import type { Action, EventBinding, Work } from '@h5/schema';
import { Field } from './Field';

const ACTION_TYPES: { label: string; value: Action['type'] }[] = [
  { label: '跳转到页', value: 'gotoPage' }, { label: '下一页', value: 'nextPage' }, { label: '上一页', value: 'prevPage' },
  { label: '打开链接', value: 'openLink' }, { label: '打开弹层', value: 'openPopup' }, { label: '关闭弹层', value: 'closePopup' },
  { label: '拨打电话', value: 'call' }, { label: '提交表单', value: 'submitForm' }, { label: '图片放大', value: 'previewImage' },
];

function defaultAction(type: Action['type'], doc: Work): Action {
  switch (type) {
    case 'gotoPage': return { type, pageId: doc.pages[0]!.id };
    case 'openLink': return { type, url: '', newWindow: false };
    case 'openPopup': return { type, popupId: doc.popups[0]?.id ?? '' };
    case 'call': return { type, phone: '' };
    case 'submitForm': return { type, formId: '' };
    case 'previewImage': return { type, src: '' };
    default: return { type } as Action;
  }
}

interface Props { value: EventBinding[]; onChange(v: EventBinding[]): void; doc: Work; currentPageId: string }

export function EventsEditor({ value, onChange, doc, currentPageId }: Props) {
  const update = (i: number, action: Action) => onChange(value.map((e, j) => (j === i ? { ...e, action } : e)));
  const remove = (i: number) => onChange(value.filter((_, j) => j !== i));
  const add = () => onChange([...value, { on: 'click', action: { type: 'nextPage' } }]);
  const pageOptions = doc.pages.map((p, i) => ({ label: p.name || `第 ${i + 1} 页`, value: p.id }));
  const popupOptions = doc.popups.map((p, i) => ({ label: p.name || `弹层 ${i + 1}`, value: p.id }));
  const formLayers = (doc.pages.find((p) => p.id === currentPageId)?.layers ?? []).filter((l) => l.type === 'form');
  const formOptions = formLayers.map((l) => ({ label: l.name || l.id, value: l.id }));

  return (
    <div>
      {value.map((ev, i) => {
        const a = ev.action;
        return (
          <div key={i} style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 8, marginBottom: 8 }}>
            <Space.Compact style={{ width: '100%', marginBottom: 8 }}>
              <Select style={{ flex: 1 }} value={a.type} options={ACTION_TYPES} onChange={(t) => update(i, defaultAction(t, doc))} />
              <Button danger icon={<DeleteOutlined />} onClick={() => remove(i)} />
            </Space.Compact>
            {a.type === 'gotoPage' && <Field label="目标页"><Select style={{ width: '100%' }} value={a.pageId} options={pageOptions} onChange={(v) => update(i, { ...a, pageId: v })} /></Field>}
            {a.type === 'openLink' && (
              <>
                <Field label="链接"><Input value={a.url} placeholder="https://" onChange={(e) => update(i, { ...a, url: e.target.value })} /></Field>
                <Field label="新窗口打开" inline><Switch size="small" checked={a.newWindow} onChange={(v) => update(i, { ...a, newWindow: v })} /></Field>
              </>
            )}
            {a.type === 'openPopup' && (
              <Field label="弹层">
                {popupOptions.length ? <Select style={{ width: '100%' }} value={a.popupId} options={popupOptions} onChange={(v) => update(i, { ...a, popupId: v })} /> : <span style={{ fontSize: 12, color: '#999' }}>暂无弹层</span>}
              </Field>
            )}
            {a.type === 'call' && <Field label="电话"><Input value={a.phone} onChange={(e) => update(i, { ...a, phone: e.target.value })} /></Field>}
            {a.type === 'previewImage' && <Field label="图片地址"><Input value={a.src} placeholder="https://" onChange={(e) => update(i, { ...a, src: e.target.value })} /></Field>}
            {a.type === 'submitForm' && (
              <Field label="表单">
                {formOptions.length
                  ? <Select style={{ width: '100%' }} value={a.formId || undefined} options={formOptions} onChange={(v) => update(i, { ...a, formId: v })} />
                  : <Input value={a.formId} placeholder="表单图层 id（本页暂无表单）" onChange={(e) => update(i, { ...a, formId: e.target.value })} />}
              </Field>
            )}
          </div>
        );
      })}
      <Button block size="small" icon={<PlusOutlined />} onClick={add}>添加事件</Button>
    </div>
  );
}
